<template>
  <LayoutLegal title="DMCA Notice">
    <template #sidebar>
      <ul class="flex flex-col gap-6 leading-snug">
        <li><a href="#1">Enrollment</a></li>
        <li><a href="#2">Creator Account</a></li>
        <li><a href="#3">Creator's Offerings</a></li>
        <li><a href="#4">Creator Proprietary Rights</a></li>
        <li><a href="#5">Payments and Charges; Revenue Share</a></li>
        <li><a href="#6">Relationship of the Parties</a></li>
        <li><a href="#7">Statements of Fact</a></li>
        <li><a href="#8">Privacy</a></li>
        <li><a href="#9">Termination/Suspension</a></li>
        <li><a href="#10">Indemnification</a></li>
      </ul>
    </template>

    <h2>Terms for Creators</h2>

    <p>
      If you have registered as a Creator at our Platform, this Terms for
      Creators will apply in addition to the General Terms of Service. If there
      is a conflict between the General Terms and this Terms for Creators, the
      terms of the latter shall prevail. Terms not otherwise defined in this
      document shall have the same meaning assigned to them in the General Terms
      of Service.
    </p>
    <p>
      You must have first agreed to the General Terms of Service in order to
      accept this Terms for Creators. By clicking on the words "I agree,"
      "Submit," or similar syntax, you are electronically signing this
      agreement, and therefore agree to be bound by and acknowledge your
      complete acceptance of all the express and incorporated terms of this
      Terms for Creators. If you do not agree to this Terms for Creators or do
      not meet the age requirements, you must not register.
    </p>
    <p>
      The Creator wants to participate in the Platform as an independent
      contractor.
    </p>
    <p>
      The parties therefore agree to the following terms in addition to those
      agreed to in the General Terms of Service:
    </p>

    <h3 id="1">ENROLLMENT</h3>
    <p>
      To be eligible to participate in the Platform as a Creator, you must be an
      individual who is at least 18-years old and has the legal capacity to
      enter into legally binding contracts.
    </p>
    <p>
      To register, you must complete the applicable registration form, submit
      one piece of government-issued picture identification that contains your
      full legal name and birth date for age and identity verification purposes,
      and a completed W-9 Form.
    </p>
    <p>
      By registering, you state that all account registration information is
      accurate.
    </p>

    <h3 id="2">CREATOR ACCOUNT</h3>
    <p>
      You must register where indicated in the Platform to receive payments as
      Creator. The information collected at the time of such registration will
      include, among other things, a contact who can be contacted with respect
      to payments. You are responsible for ensuring that the information
      provided for purposes of receiving payment is kept up to date at all
      times.
    </p>
    <p>
      If you designate an entity to receive payments for the Content you post
      under your Account, you are required to show that you have a majority
      interest control over such entity, as you may not designate unrelated
      third parties to receive payment for your Creator's Offerings.
    </p>
    <p>
      All payments made to you by us pursuant to the Terms of Service will be
      made and deposited into your Account with us. Unless you have a balance in
      your Account below the amount we have in effect as set out in the
      Platform, you may withdraw funds from your Account at anytime and for any
      amount not exceeding the balance in your Account. If the balance in your
      Account is below the amount we have in effect at the time you would like
      to withdraw such funds, you may withdraw the full amount of your balance
      on specific days of the month as set out in the Platform.
    </p>
    <p>
      All remittances of funds made to you will be made via electronic funds
      transfer. You will not be paid through checks or cash, unless no
      electronic funds transfer can be made to you. In such case, we will pay
      you by check sent to the address you have registered with the Platform.
      You must provide notice of any nonpayment, or payment disputes, by sending
      us an email to the following address: at
      <a href="mailto:support@favoritely.com">support@favoritely.com</a>
    </p>
    <p>
      WE MAY, IN OUR SOLE DISCRETION, LIMIT, SUSPEND, TERMINATE, MODIFY, OR
      DELETE ACCOUNTS OR ACCESS TO THE PLATFORM OR ANY PORTION OF IT; PROHIBIT
      ACCESS TO OUR PLATFORM, AND ITS CONTENT, AND TOOLS; OR DELAY OR REMOVE
      HOSTED CONTENT, AND WE UNDER NO OBLIGATION ARE REQUIRED TO COMPENSATE YOU
      FOR ANY LOSSES OR RESULTS. YOU MAY NOT BRING ANY CLAIM AGAINST FAVORITELY
      LLC ("FLLC") FOR LIMITING ACCESS TO OUR PLATFORM, SUSPENSION, TERMINATION,
      OR DELETION OF ANY ACCOUNT FOR ANY REASON. IF ANY SUCH CLAIM IS BROUGHT
      AGAINST US, YOU ARE RESPONSIBLE FOR ALL DAMAGES CAUSED TO FLLC, INCLUDING
      ATTORNEY FEES AND COSTS. THESE TERMS REMAIN IN EFFECT IF THE ACCOUNT IS
      ACTIVE OR INACTIVE OR YOU NO LONGER HAVE AN ACCOUNT.
    </p>

    <h3 id="3">CREATOR'S OFFERINGS</h3>
    <p>
      As your Creator's Offering, you may upload to or display through your
      Account various media, content, and material including videos,
      photographs, audio, text, audiovisual, graphics, music, trademarks, logos,
      and artwork. By uploading your Content to or displaying it through your
      Account, you represent that such Content in your Creator's Offering is
      your own or you have a license to publish, publicly display, publicly
      perform, distribute, and you authorize us and the Platform users to use
      such Content as contemplated by the Terms of Service.
    </p>
    <p>
      You may use various interactive features, including, messaging, and
      comments sections designed to foster interactions between the Creator and
      Users (the "Interactive Services"). However, you will remain responsible
      for all feedback transmitted by you or your Users through the Interactive
      Services under your Account. Any Content shared through your Interactive
      Services, whether yours or from your Users is subject to the Terms of
      Service.
    </p>
    <p>
      Your Account, your Content, your Creator's Offerings, and Interactive
      Services, in addition to other terms set in the Terms of Service, must
      not:
    </p>

    <ul class="leading-loose ml-6">
      <li>
        Contain any material that is harmful, threatening, defamatory, libelous,
        slanderous, obscene, indecent, scatological, abusive, offensive,
        harassing, violent, hateful, inflammatory, or otherwise objectionable;
      </li>
      <li>
        Show or depict minors under 18 years old or in any way harm, or attempt
        to harm any individual under 18 years old;
      </li>
      <li>
        Promote sexually explicit violence, or discrimination based on race,
        sex, religion, nationality, disability, sexual orientation, or age;
      </li>
      <li>
        Intentionally publish non-consensual sexually explicit material of a
        person without their permission, or what is known as "revenge porn;"
      </li>
      <li>Promote the use of firearms or weapons;</li>
      <li>
        Promote or glamorize alcohol abuse, illegal drug use or use of tobacco
        products;
      </li>
      <li>Contain misleading or fraudulent claims;</li>
      <li>
        Infringe any copyright, patent, service mark, trademark, trade name,
        trade secret, or other intellectual property or proprietary rights of
        any other person;
      </li>
      <li>
        Violate the legal rights (including the rights of publicity and privacy)
        of others or contain any material that could give rise to any civil or
        criminal liability under applicable law or otherwise may be in conflict
        with this agreement or the Terms of Service;
      </li>
      <li>Be likely to deceive any person;</li>
      <li>
        Discuss, facilitate, promote, advertise, or solicit any illegal
        activity, or advocate, discuss, facilitate, promote, advertise, solicit,
        or assist any unlawful act, including prostitution or sex trafficking;
      </li>
      <li>
        Cause annoyance, inconvenience, or needless anxiety or be likely to
        upset, embarrass, alarm, or annoy any other person;
      </li>
      <li>
        Impersonate any person, or misrepresent the Creator's identity or
        affiliation with any individual or organization;
      </li>
      <li>
        Display any telephone numbers, street addresses, last names, email
        addresses, URLs, or any confidential information of any person;
      </li>
      <li>
        Display any images or videos containing confidential or personal
        information without authorization;
      </li>
      <li>
        Give the impression that it emanates from or is endorsed by FLLC or any
        other individual or entity, if this is not the case; or
      </li>
      <li>
        Contain technically harmful material, including computer viruses, logic
        bombs, Trojan horses, worms, malware, ransomware, harmful components,
        corrupted data, or other malicious software or harmful data.
      </li>
    </ul>

    <p>
      Engaging in any of the above practices will be considered a material
      breach of the Terms of Service and may result in immediate termination of
      the your Account and access to the Platform without notice, in our sole
      discretion. We may pursue any legal remedies against you if you engage in
      any of the above prohibited conducts. We will also report any Creator's
      Offering that could be considered exploitative of children in any way, if
      in the U.S., to the National Center for Missing and Exploited Children's
      Cyber Tip line; or any other applicable legal and regulatory bodies.
    </p>
    <p>
      We fully cooperate with law enforcement authorities and court orders
      requesting or directing us to disclose the identity or other information
      of anyone posting any content on the Platform. You hereby waive any claims
      you might have against us and our Affiliate Entities, licensees, and
      service providers, resulting from any action taken by or against us during
      or because of our investigations and from any actions taken as a
      consequence of investigations by either us, any third party or law
      enforcement authorities.
    </p>
    <p>
      We will not endorse the opinions expressed your Creator's Offerings, your
      Content, or the Interactive Services under your Account. We may reject or
      remove any Creator's Offering, Content, or Creator Account at any time and
      for any reason. The reasons for rejection may include where we believe
      that the Creator's Offering, Content, or Creator Account breaches the
      Terms of Service. We will remove objectionable content that violates the
      law or any rules governed by our merchant account processing contracts. We
      will not be liable for any action or inaction regarding transmissions,
      communications, or content provided by any person. We will not be liable
      to anyone for performance or non-performance of the activities described
      in this section 3.
    </p>

    <h3 id="4">CREATOR PROPRIETARY RIGHTS</h3>
    <p>
      We do not claim any ownership rights in your Creator's Offerings or
      Content depicted under your Account or the Interactive Services. You
      continue to retain any ownership rights that you may have in those
      Creator's Offerings or Content.
    </p>
    <p>
      However, by posting your Content in the Platform, you grant FLLC and
      Affiliate Entities, and service providers, and each of their and our
      respective licensees a perpetual, sub licensable, transferable,
      irrevocable, worldwide, royalty free license to use, reproduce, modify,
      prepare derivative works of, publicly perform, publicly display, and
      distribute any of your Creator's Offerings or Content that you upload to,
      post, display on, stream, broadcast, or offer for sale through your
      Account or the Interactive Services. This license includes the right to
      use your Content and any associated trademarks, service marks, or trade
      names to promote and redistribute any part of the Platform -- and
      derivative works of it -- in any media formats and through any media
      channels.
    </p>
    <p>
      Likewise, you hereby grant us, and our service providers, a perpetual, sub
      licensable, transferable, irrevocable, worldwide, royalty-free license to
      use your name and likeness in any manner and any media, throughout the
      world, at any time, for advertising and promotional purposes and any other
      lawful purpose. You acknowledge that your name and likeness may appear on
      platforms that contain adult-oriented, sexually explicit materials,
      including material that you might consider obscene, offensive, or
      otherwise objectionable. You hereby waive any right to inspect or approve
      our use of the your name and likeness.
    </p>

    <p class="underline">Moral Rights Waiver</p>
    <p>
      You hereby waive all moral rights in your Creator's Offerings and your
      proprietary Content that may be available to you in any part of the world,
      and you state that no moral rights have been asserted.
    </p>

    <h3 id="5">MODEL RELEASE</h3>
    <p>
      When you post Content through your account to the Platform that includes
      people other than the Creator, you must ensure that you obtain a Model
      Release form. The Model Release form must be kept in the possession of the
      Creator and must be provided to us upon request. The Model Release must be
      provided within three (3) days of the request.
    </p>
    <p>
      Other people appearing in your Creator's Offerings must sign up for their
      own Account to the Platform, become a Creator and age verify, and then you
      must connect or associate their Account with yours in order to post
      Content of them to your Account.
    </p>

    <h3 id="6">PAYMENTS AND CHARGES; REVENUE SHARE</h3>
    <p>
      When you post Content through your Account to the Platform, you must
      include a price that you will charge your Users for such Creator's
      Offering. The price may not exceed the maximum price that we have in
      effect as set out in the Platform, at the time of submission. You may
      change the price for each Creator's Offering by following the process, and
      providing the required prior notice, set out in the Platform.
      Alternatively, you may publish your Creator's Offering to be offered to
      Users without charge.
    </p>

    <p class="underline">Creator Revenue</p>
    <p>
      You will receive 80% of the Net Revenue (the "Creator Revenue") from the
      sale of your Creator's Offerings to your Users; we will retain 20% of that
      Net Revenue as a service fee. "Net Revenue" means gross revenue actually
      received from end users from a Creator's Offering transaction less
      transactional costs, chargebacks, and refunds and excluding any taxes
      (including VAT). "Transactional costs" are amounts the Company pays to
      third parties to facilitate a transaction and may include
      payment-processing fees, foreign exchange fees, and third-party platform
      fees.
    </p>
    <p>We will deposit the Creator Revenue in the your Account.</p>

    <p class="underline">Transaction Taxes</p>
    <p>
      You are responsible for paying all taxes owed for revenues earned under
      the Terms of Service. We may withhold from any amounts due to you that we
      may determine from time to time in good faith must be withheld under U.S.
      or any applicable tax law. We may file reports concerning income with any
      taxing authority, including the U.S. Internal Revenue Service.
    </p>

    <p class="underline">Limitations</p>
    <p>
      We are not required to payout Creator Revenue with respect to any Content
      Purchase that is subject to a refund or chargeback. We may withhold
      amounts or make adjustments in our sole discretion (a) to satisfy any
      refunds that it may make (including for technical errors); (b) pending any
      investigation of fraud or other illegality; (c) due to excessive
      chargebacks or refunds; (d) due to breach of the Terms of Service
      (including Prohibited Uses); and (e) as required by U.S. or any applicable
      tax laws. If after six months we are unable to payout you as a result of
      inaccurate information in the your Account and we are unable to contact
      you, that Creator Revenue will be forfeited and become our property.
    </p>

    <p class="underline">Disputes</p>
    <p>
      If you dispute the calculation of the Creator Revenue, you will notify us
      in writing no later than 15 days after the disputed calculation. Failure
      to notify us within this period will result in your waiver of any claims
      related to the disputed calculation.
    </p>

    <h3 id="7">RELATIONSHIP OF THE PARTIES</h3>
    <p>
      The parties intend that their relationship will be that of independent
      contractors and not business partners. This agreement does not, and the
      parties do not intend it to, create a partnership, joint venture, agency,
      franchise, or employment relationship between the parties and the parties
      expressly disclaim the existence of any of these relationships between
      them. Neither party is the agent for the other, nor does either party have
      the right to bind the other on any agreement with another person.
    </p>
    <p>
      The Creator is not eligible under this agreement to participate in any
      vacation, group medical or life insurance, disability, profit sharing or
      retirement benefits, or any other fringe benefits or benefit plans offered
      by the Company to its employees.
    </p>
    <p>
      The Company is not responsible for withholding or paying any income,
      payroll, Social Security, or other foreign, federal, state, or local
      taxes; making any insurance contributions, including unemployment or
      disability; or obtaining worker's compensation insurance on the Creator's
      behalf. The Creator is solely responsible for all those taxes or
      contributions, including penalties and interest. The Creator is also
      responsible for maintaining adequate worker's compensation coverage or
      insurance for the Creator and any employee or other personnel engaged by
      the Creator.
    </p>
    <p>
      Any persons employed or engaged by the Creator in connection with the
      creation, production, or submission of your Content must be the Creator's
      employees or contractors and the Creator will be fully responsible for
      them and indemnify the Company against any claims made by or on behalf of
      any such employee or contractor. The Creator will require each such
      employee and contractor to sign written agreements securing for the
      Company all rights granted to the Company in this agreement before that
      employee or contractor participates in the creation or submission of any
      Content in connection with this agreement.
    </p>

    <h3 id="8">STATEMENTS OF FACT</h3>
    <p>
      The Creator states that the following facts are accurate and will continue
      to be accurate during this agreement:
    </p>
    <ul class="leading-loose ml-6">
      <li>
        The Creator has independently evaluated the desirability of
        participating in the Platform, and the Creator has not relied on any
        statement other than those stated in the Terms of Service.
      </li>
      <li>
        The Creator's acceptance of this Terms for Creators and the General
        Terms of Service will not conflict with or violate (i) any order,
        judgment, or decree that applies to the Creator; or (ii) any agreement
        that applies to the Creator.
      </li>
    </ul>

    <p>
      The Creator's performance under the Terms of Service, the Creator's use of
      the Platform, the Creator's Offerings, and all other material, content,
      products, or services made available on, or linked to or from, a Creator's
      Account or the Interactive Services, will not:
    </p>

    <ul class="leading-loose ml-6">
      <li>
        invade the right of privacy or publicity of any person (including
        invasion of rights of celebrity);
      </li>
      <li>
        involve any defamatory, libelous, slanderous, sexually explicit,
        obscene, indecent, scatological or otherwise unlawful material;
      </li>
      <li>violate any applicable law; or</li>
      <li>
        otherwise infringe on the rights of any third parties, including those
        of copyright, patent, trademark, service mark, trade name, trade secret,
        or other intellectual-property rights, or engage in false advertising,
        unfair competition, violation of antidiscrimination law, or violation of
        any other right of any person.
      </li>
    </ul>

    <p>
      The Creator has the right to grant the licenses granted in section 4 to us
      and our Affiliate Entities and service providers, and each of their and
      our respective licensees, successors, and assigns.
    </p>
    <p>
      The Creator has a signed written consent or release for each identifiable
      person displayed in the Creator's Content to use their name and likeness
      to allow inclusion and use of the Creator's Content in the way
      contemplated by the Terms of Service.
    </p>
    <p>
      Neither the Creator's Offerings nor your Content displays or depicts any
      person under 18-years old at the time of production.
    </p>
    <p>
      Your Account, your Content, and the Creator's Offerings otherwise comply
      with section 3 and any other applicable terms set forth elsewhere in the
      Terms of Service.
    </p>

    <h3 id="9">PRIVACY</h3>
    <p>
      For information about how FLLC collects, uses, and shares the Creator's
      personal information, please review the Platform's Privacy Policy. The
      Creator acknowledges that FLLC may retain indefinitely information the
      Creator submits to FLLC, including the Creator's identification, in case
      the information is needed to comply with applicable law or in a good-faith
      belief that preservation or disclosure of that information is reasonably
      necessary in the FLLC's opinion to (a) comply with legal process,
      including civil and criminal subpoenas, court orders, or other compulsory
      disclosure; (b) enforce this agreement; (c) respond to claims of a
      violation of the rights of third parties, regardless of whether the third
      party is a user, individual, or government agency; or (d) protect the
      rights, property, or personal safety of FLLC, the Platform's users, or the
      public.
    </p>

    <h3 id="10">TERMINATION/SUSPENSION</h3>
    <p>
      <span class="underline">Account Deletion</span>
      . You may delete your Creator's Account at any time. You may also delete
      your Creator's Offerings or Content at any time, except that the Creator's
      Offerings will continue to be made available to Users who purchased those
      Creator's Offerings.
    </p>
    <p>
      <span class="underline">Termination on Notice</span>
      . Either party may terminate this agreement at any time as set forth in
      the Terms of Service.
    </p>
    <p>
      <span class="underline">Effect of Termination</span>
      . On termination, your right to access the Platform and all licenses
      granted by us ends, and we will payout to you all revenues collected under
      this agreement less the service fees and any other amounts that we are
      entitled to withhold under the Terms of Service or set off against any
      loss incurred under section 10 (Indemnification). If your participation in
      the Platform terminates or is suspended for any reason, we may, without
      notice, terminate or suspend your Account and promptly remove any Content
      or Creator's Offering, in our discretion, at any time afterwards. On
      deletion of the your Account, the Account will initially be hidden for 30
      days and no new Content Purchases will be allowed under such Account.
      After the Account has been hidden, the Account will be deleted unless you
      notify us otherwise, except that the Creator's Offerings will continue to
      be made available to Users who purchased those Creator's Offerings.
    </p>
    <p>
      <span class="underline">Survival</span>
      . Any part of the Terms of Service that imposes an obligation after
      termination will survive the termination, including all warranty
      disclaimers and limitations of liability.
    </p>
    <p>
      FLLC may, in its sole discretion, terminate or suspend your access to all
      or part of the Platform at any time, with or without notice, for any
      reason or no reason at all, including, without limitation, violation of
      any term of this Agreement. Any activity which we believe, in our sole
      discretion, to even possibly be illegal, fraudulent, abusive, or in
      violation of our community standards, or our rules, may be grounds for
      termination of your access to all or part of the Platform. In those cases,
      we reserve the right to refer any such activity to any appropriate law
      enforcement agencies. Termination of an Account will be paid for any
      tokens remaining in the Account at the time of termination, as long as the
      amount in the respective account exceeds payment minimums, paid according
      to payment information on file and on the next scheduled payout date. If
      the amount remaining in the account does not exceed payment minimums, the
      amount is forfeited.
    </p>

    <h3 id="11">INDEMNIFICATION</h3>
    <p>
      <span class="underline">In General</span>
      . The Creator will pay the Company for any loss of the Company that is
      caused by the Creator's (a) use of the Platform or the Interactive
      Services, including uploading, submitting, or offering for sale any
      Creator's Offering or Content; (b) breach of the Terms of Service,
      including any statement of fact; (c) failure to pay taxes in connection
      with Creator Revenues earned under this agreement; (d) dispute with any
      fan or any other person; (e) infringement of another person's
      intellectual-property rights; (f) violation of any applicable law; or (g)
      tortious acts or omissions. But the Creator is not required to pay if the
      loss was caused by the Company's intentional misconduct.
    </p>

    <p class="underline">Definitions</p>
    <p>
      "Loss" means an amount that the Company is legally responsible for or pays
      in any form. Amounts include, for example, a judgment, a settlement, a
      fine, damages, injunctive relief, staff compensation, a decrease in
      property value, and expenses for defending against a claim for a loss
      (including fees for legal counsel, expert witnesses, and other advisers).
      A loss can be tangible or intangible; can arise from bodily injury,
      property damage, or other causes; can be based on tort, breach of
      contract, or any other theory of recovery; and includes incidental,
      direct, and consequential damages.
    </p>
    <p>
      A loss is "caused by" an event if the loss would not have occurred without
      the event, even if the event is not a proximate cause of the loss.
    </p>
    <ul class="leading-loose ml-6">
      <span class="underline">Company's Duty to Notify</span>
      . The Company will notify the Creator before the 15th business day after
      the Company knows or should reasonably have known of a claim for a loss
      that the Creator might be obligated to pay. The Company's failure to give
      the Creator timely notice does not terminate the Creator's obligation,
      except to the extent that the failure prejudices the Creator's ability to
      defend the claim or mitigate losses.
    </ul>
    <p class="underline">Legal Defense of a Claim</p>
    <ul class="leading-loose ml-6">
      <li>
        <span class="underline">Company's Control</span>
        . The Company has control over defending a claim for a loss (including
        settling it), unless the Company directs the Creator to control the
        defense.
      </li>
      <li>
        <span class="underline">Direction to Control</span>
        . If the Company directs the Creator to control the defense, each of the
        following applies:
      </li>
      <li>The Creator may choose and retain legal counsel.</li>
      <li>The Company may retain its own legal counsel at its expense.</li>
      <li>
        The Creator will not settle any litigation without the Company's written
        consent if the settlement (1) imposes a penalty or limitation on the
        Company, (2) admits the Company's fault, or (3) does not fully release
        the Company from liability.
      </li>
      <li>
        <span class="underline">Good Faith</span>
        . The Company and the Creator will cooperate with each other in good
        faith on a claim.
      </li>
      <li>
        <span class="underline">No Exclusivity</span>
        . The Company's rights under this section 10 do not affect other rights
        that the Company might have.
      </li>
    </ul>
  </LayoutLegal>
</template>

<script setup>
  definePageMeta({
    skipAuth: true,
  })

  useHead({
    title: "Terms for Creators - favoritely.com",
    meta: [{ name: "description", content: "favoritely.com" }],
  })
</script>
